import React from "react";
import { CardHeader } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { useSelector } from 'react-redux';
import HasRole from "src/utils/has-role";
import { useHistory } from 'react-router-dom';
import Background from './bg';
import domain from 'src/config/domain';

import {
  ArrowDownIcon,
  ArrowsPointingOutIcon,
  BoltSlashIcon,
  BuildingLibraryIcon,
  ChevronRightIcon,
  InboxIcon,
} from "@heroicons/react/24/outline";

const ActionCenter = ({ data, loading, group }) => {
  const { account: { account } } = useSelector((state) => state);
  const history = useHistory();
  const domainConfig = domain();

  let authorisedTransactionCount = 0;

  if (data?.transactions?.status?.weekly) {
    for (const item of data?.transactions?.status?.weekly) {
      if (item.status === 'AUTHORIZED') {
        authorisedTransactionCount = item.count;
      }
    }
  }

  const hasOpenBankingRole = HasRole(account, ['open-banking:manage']);

  let actionsCount = (data?.chargebacks?.last_seven_days > 0 ? 1 : 0)
    + (data?.payment_links?.unassigned > 0 ? 1 : 0)
    + (data?.payment?.greater_seven_days > 0 ? 1 : 0)
    + (authorisedTransactionCount > 0 ? 1 : 0)
    + (data?.payment_links?.greater_seven_days > 0 ? 1 : 0)
    + (data?.pre_auth?.pending > 0 ? 1 : 0);

  let actionsTotal = loading === true || Object.keys(data).length === 0 ? 0 : (data?.chargebacks?.last_seven_days || 0)
    + (data.payment_links?.unassigned || 0)
    + (data.payment?.greater_seven_days || 0)
    + (authorisedTransactionCount || 0)
    + (data?.payment_links?.greater_seven_days || 0)
    + (data?.pre_auth?.pending > 0 ? 1 : 0);

  if (hasOpenBankingRole) {
    actionsCount += data?.open_banking?.expired_connections > 0 ? 1 : 0;
    actionsTotal += data?.open_banking?.expired_connections || 0;
  }

  const openBankingLinkAction = () => {
    if (group === true) {
      document.getElementById('expiredopenbanking').scrollIntoView();
    } else {
      history.push('/organisation/bank');
    }
  }

  return (
    <div className={`flex flex-col rounded-tr-2xl rounded-bl-2xl overflow-hidden border animate-in fade-in-50 mb-2 ${actionsTotal > 0 ? 'border border-red-200' : ''} `}>
      <CardHeader className={`relative border-bottom ${actionsTotal > 0 ? 'bg-gradient-to-r from-red-200 via-red-200 to-red-300 ' : 'bg-gray-50'}`}>
        {actionsTotal > 0 && (
          <React.Fragment>
            <Background className="absolute top-0 left-0 w-100 h-100 z-10" />
            <div className="float-right text-sm -mt-0.5 -mr-1 z-20">
              <span
                className="relative inline-flex items-center gap-x-1.5 py-1.5 px-3 text-xs bg-rose-100 text-red-900 !font-bold rounded-lg !border !border-rose-100">
                <span className="!font-extrabold">{actionsCount}</span> item(s)</span>
            </div>
          </React.Fragment>
        )}

        <h2 className={`relative text-xl z-20 font-normal ${actionsTotal > 0 ? 'text-rose-900' : `${domainConfig.styles.textColour}`}`}>
          Action <span className="font-bold">Center</span>
        </h2>
      </CardHeader>
      {" "}
      <div className="card-inner-group">

        {loading && (
          <div className="p-4">
            <div className="text-center fs-20px w-100">
              <Skeleton count={2} height="70px" />
            </div>
          </div>
        )}

        {(loading === false && actionsTotal === 0) && (
          <div className="text-center mt-5 mb-5">
            You are up to date
          </div>
        )}

        {loading === false && hasOpenBankingRole === true && data.open_banking && data.open_banking.expired_connections > 0 && (
          <div className="card-inner py-2">
            <div className="nk-wg-action cursor-pointer" onClick={openBankingLinkAction}>
              <div className="nk-wg-action-content pl-12">
                <BuildingLibraryIcon className="icon !text-red-900 w-6 h-6 mt-[6px]" />
                <div className="title text-red-800 !font-bold pb-0">Open Banking Connections</div>
                <p className="!text-slate-800">
                  You have <strong className="!text-red-900">{data.open_banking.expired_connections}</strong> expired open banking connection(s).
                </p>
              </div>
              <ChevronRightIcon className="w-6 h-6 text-red-900" />
            </div>
          </div>
        )}

        {loading === false && data.chargebacks && data.chargebacks.last_seven_days > 0 && (
          <div className="card-inner py-2">
            <div className="nk-wg-action cursor-pointer" onClick={() => history.push('/chargebacks')}>
              <div className="nk-wg-action-content pl-12">
                <ArrowDownIcon className="icon !text-red-900 w-6 h-6 mt-[6px]" />
                <div className="title text-red-800 !font-bold pb-0">Chargebacks</div>
                <p className="!text-slate-800">
                  You have <strong className="!text-red-900">{data.chargebacks.last_seven_days}</strong> new chargeback{data.chargebacks.last_seven_days > 1 ? 's' : ''} in the last 7 days.
                </p>
              </div>
              <ChevronRightIcon className="w-6 h-6 text-red-900" />
            </div>
          </div>
        )}

        {loading === false && data.pre_auth && data.pre_auth.pending > 0 && (
          <div className="card-inner py-2">
            <div className="nk-wg-action cursor-pointer" onClick={() => history.push('/payments')}>
              <div className="nk-wg-action-content pl-12">
                <ArrowsPointingOutIcon className="icon !text-red-900 w-6 h-6 mt-[6px]" />
                <div className="title text-red-800 !font-bold pb-0">Pre-Authorised Transactions</div>
                <p className="!text-slate-800">
                  You have <strong className="!text-red-900">{data.pre_auth.pending}</strong> pending pre-authorisations.
                </p>
              </div>
              <ChevronRightIcon className="w-6 h-6 text-red-900" />
            </div>
          </div>
        )}

        {loading === false && data.payment_links && data.payment_links.unassigned > 0 && (
          <div className="card-inner py-2">
            <div className="nk-wg-action cursor-pointer" onClick={() => history.push('/payment-links?has_booking_reference=0')}>
              <div className="nk-wg-action-content pl-12">
                <BoltSlashIcon className="icon !text-red-900 w-6 h-6 mt-[6px]" />
                <div className="title text-red-800 !font-bold pb-0">Unassigned Payment Links</div>
                <p className="!text-slate-800">
                  You have <strong className="!text-red-900">{data.payment_links.unassigned}</strong> payment links with no booking reference.
                </p>
              </div>
              <ChevronRightIcon className="w-6 h-6 text-red-900" />
            </div>
          </div>
        )}

        {loading === false && authorisedTransactionCount > 0 && (
          <div className="card-inner py-2">
            <div className="nk-wg-action cursor-pointer" onClick={() => history.push('/payments')}>
              <div className="nk-wg-action-content pl-12">
                <ArrowsPointingOutIcon className="icon !text-red-900 w-6 h-6 mt-[6px]" />
                <div className="title text-red-800 !font-bold pb-0">Authorised Transactions</div>
                <p className="!text-slate-800">
                  You have <strong className="!text-red-900">{authorisedTransactionCount}</strong> unsettled authorised transactions.
                </p>
              </div>
              <ChevronRightIcon className="w-6 h-6 text-red-900" />
            </div>
          </div>
        )}

        {loading === false && data.payment_links && data.payment_links.greater_seven_days > 0 && (
          <div className="card-inner py-2">
            <div className="nk-wg-action cursor-pointer" onClick={() => history.push('/payment-links?status=active')}>
              <div className="nk-wg-action-content pl-12">
                <InboxIcon className="icon !text-red-900 w-6 h-6 mt-[6px]" />
                <div className="title text-red-800 !font-bold pb-0">Pending Payment Links</div>
                <p className="!text-slate-800">
                  You have <strong className="!text-red-900">{data.payment_links.greater_seven_days}</strong> pending payment links older than 7 days.
                </p>
              </div>
              <ChevronRightIcon className="w-6 h-6 text-red-900" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default ActionCenter;
